import { useEffect, useState } from "react"

/**
 * The design's light/dark switch (shared with apps/web/src/hooks/use-theme.ts).
 *
 * A HOOK IN ITS OWN FILE because react-refresh only works when a module exports
 * components alone — a hook exported beside a header component makes every theme
 * change remount the header instead of re-rendering it.
 *
 * The design ships a full dark theme: every token has a
 * :root[data-theme="dark"] value. Shipping those without a switch would mean
 * half the design existed and could not be reached.
 */

type Theme = "light" | "dark"

const STORAGE_KEY = "swamithra-panel-theme"

function initialTheme(): Theme {
	if (typeof window === "undefined") return "light"
	const stored = window.localStorage.getItem(STORAGE_KEY)
	if (stored === "light" || stored === "dark") return stored
	// No stored choice means follow the device, which is what the design's own
	// prefers-color-scheme block does for anyone who never touches the control.
	//
	// Guarded: jsdom does not implement matchMedia, and an unguarded call throws
	// during render — which takes out every test that mounts a screen, with a
	// message about the screen rather than about the media query.
	return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true ? "dark" : "light"
}

export function useTheme(): [Theme, () => void] {
	const [theme, setTheme] = useState<Theme>(initialTheme)

	useEffect(() => {
		// The attribute, not a class: tokens.css keys its dark values off
		// :root[data-theme="dark"], and that selector is the design's.
		document.documentElement.setAttribute("data-theme", theme)
		window.localStorage.setItem(STORAGE_KEY, theme)
	}, [theme])

	return [
		theme,
		() => {
			setTheme(current => (current === "dark" ? "light" : "dark"))
		},
	]
}
